import React, { useEffect } from 'react';
import { Router } from '@uhg-abyss/web/ui/Router';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { StandardUserRegistration } from './Content/StandardUserRegistration';
import { useUser } from '@src/state/user';
import { usePAARegistration } from '@src/state/PAARegistration';
import { useStandardUserRegistration } from '@src/state/SURegistration';
import { useVerifyClaim } from '@src/state/verifyClaim';
import { useTIN } from '@src/state/taxInformation';
import { useMedicaid } from '@src/state/medicaid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { registerAnalytics } from '@src/common/tools/analytics';
import { CancelRegistration } from '@src/common/CancelRegistration';
import { FeedbackTab } from '@src/common/FeedbackTab';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { config } from '@uhg-abyss/web/tools/config';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { useLocation } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { Header } from './Content/Header';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Footer } from './Content/Footer/Footer';
import { Landing } from './Content/Landing';
import { TaxInformation } from './Content/TaxInformation';
import { ErrorComponent } from './Content/ErrorComponent';
import { SUEmailConfirmation } from './Content/StandardUserRegistration/EmailConfirmation';
import { LockMessageComponent } from './Content/PAARegistration/LockMessageComponent';
import {
  PAARegistration,
  PAADesignate,
  EmailConfirmation as PAAEmail,
} from './Content/PAARegistration';
import { PAARequiredMessage } from './Content/PAARegistration/PAADesignate/PAARequiredMessage';
import { MaintenanceMessage } from './Content/MaintenanceMessage/MaintenanceMessage';

const MainContainer = styled('div', {
  background: '$gray2',
  paddingBottom: 30,
  flex: '1 0 auto',
  minHeight: '85vh',
});

const Body = styled('div', {
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
});

export const App = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const router = useRouter();

  const { getUserSaga } = useUser.state();
  const { TINInfoSaga } = useTIN.state();
  const { getVerifyMedicaidSaga } = useMedicaid.state();
  const { submitPATINSaga } = usePAARegistration.state();
  const submitSUTINSaga = useStandardUserRegistration.actions();
  const { isAPIMarketPlaceUser } = useUser.state();
  const {
    paymentInfoSaga,
    tinAddressSaga,
    validateSecurityKeySaga,
    getSecurityKeySaga,
    claimSaga,
  } = useVerifyClaim.state();
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const { search, pathname } = useLocation();

  useEffect(() => {
    registerAnalytics(
      featureToggles.getRow('US4847744_LIVEPERSON_CHAT_REGISTRATION')?.active,
      uiMessages.getRow('QUALTRICS_SURVEY_SCRIPT')?.message ||
        config('QUALTRICS_SURVEY_SCRIPT'),
      uiMessages.getRow('LIVE_CHAT_URL')?.message
    );
  }, [featureToggles.getRow('US4847744_LIVEPERSON_CHAT_REGISTRATION')?.active]);

  useEffect(() => {
    registerAnalytics(
      featureToggles.getRow('US4847744_LIVEPERSON_CHAT_REGISTRATION')?.active,
      uiMessages.getRow('QUALTRICS_SURVEY_SCRIPT')?.message ||
        config('QUALTRICS_SURVEY_SCRIPT'),
      uiMessages.getRow('LIVE_CHAT_URL')?.message
    );
    const prevToken = pathname.substring(pathname.lastIndexOf('/') + 1);
    let tokenInfo = '';
    if (prevToken) {
      tokenInfo = prevToken;
    } else if (search.includes('isApiMarketPlaceUser=true')) {
      tokenInfo = tokenizer.create({ sourceSystem: 'apim' });
    } else {
      tokenInfo = tokenizer.create(tokenInfo);
    }
    router.navigate(`/${tokenInfo}`);
  }, []);

  const pageTitle = `{{title}}${
    isAPIMarketPlaceUser ? ' | API Marketplace' : ' | UHC Provider Portal'
  }`;

  return (
    <Body>
      <Header />
      <LoadingOverlay
        loadingMessage="Please wait while we retrieve your information."
        isLoading={
          getUserSaga.isLoading ||
          TINInfoSaga.isLoading ||
          getVerifyMedicaidSaga.isLoading ||
          claimSaga.isLoading ||
          paymentInfoSaga.isLoading ||
          submitPATINSaga.isLoading ||
          submitSUTINSaga.isLoading ||
          tinAddressSaga.isLoading ||
          validateSecurityKeySaga.isLoading ||
          getSecurityKeySaga.isLoading
        }
      >
        <MainContainer>
          {featureToggles.getRow('DIGISEC_MAINTENANCE_PAGE')?.active ? (
            <MaintenanceMessage />
          ) : (
            <React.Fragment>
              <Router.Routes title={pageTitle}>
                <Router.Route
                  path="/:token"
                  element={<Landing title="dashboard" />}
                />
                <Router.Route
                  path="/TINValidation/:sourceTile/:token"
                  element={<TaxInformation title="TIN Information" />}
                />
                <Router.Route
                  path="/SURegistration/:registrationStep/:token"
                  element={
                    <StandardUserRegistration title="User Registraton" />
                  }
                />
                <Router.Route
                  exact
                  path="/PAADesignate/:token"
                  element={<PAADesignate title="Designate PAA" />}
                />
                <Router.Route
                  path="/PAARegistration/:registrationStep/:token"
                  element={<PAARegistration title="PAA Attestation" />}
                />
                <Router.Route
                  path="/PAADesignate/EmailConfirmation/:token"
                  element={<PAAEmail title="PAA Confirmed" />}
                />
                <Router.Route
                  path="/Registration/EmailConfirmation/:token"
                  element={<SUEmailConfirmation title="Email Confirmed" />}
                />
                <Router.Route
                  path="/error-page/:token"
                  element={<ErrorComponent title="Not Responding" />}
                />
                <Router.Route
                  path="/PAAlockMessage-page/:errorSource/:token"
                  element={<LockMessageComponent title="Unable to Verify" />}
                />
                <Router.Route
                  path="/cancel_registration/:token"
                  element={
                    <CancelRegistration title="Registration cancelled" />
                  }
                />
                <Router.Route
                  path="/PAARequired/:token"
                  element={<PAARequiredMessage title="PAA Required" />}
                />
              </Router.Routes>
            </React.Fragment>
          )}
        </MainContainer>
        <FeedbackTab />
      </LoadingOverlay>
      <Footer />
    </Body>
  );
};
